import { useEffect, useMemo } from 'react';
import { useAccesPort, usePref } from '@taki/ui';

import { DOCK_APPS_MAX } from './Dock.js';
import { useDeprecatedAppIds } from './buildStamp.js';
import type { ShellAppEntry } from './types.js';

/**
 * L'EPINGLAGE AUTOMATIQUE D'UNE APP NOUVELLEMENT INSTALLEE.
 *
 * LE DEFAUT QU'IL CORRIGE, et il n'avait rien d'evident a l'ecran. Tant que la
 * pref `dock.apps` est absente, le dock montre les premieres apps installees :
 * une app de plus apparait toute seule. Des que quelqu'un touche au panneau
 * Dock des Parametres, la pref devient une LISTE FERMEE, et toute app installee
 * ensuite reste invisible dans le rail, pour toujours, sans que rien ne le dise.
 * Le symptome ("mon app n'est pas dans le dock") ne ressemble pas a sa cause
 * ("tu as personnalise le dock il y a trois semaines").
 *
 * CE QU'IL FAUT DISTINGUER, ET C'EST TOUT LE SUJET : une app qu'on vient
 * d'INSTALLER, et une app deja installee qui redevient VISIBLE (masquage DEV
 * leve, droit ouvert par un administrateur, app sortie du rebut). La premiere
 * merite une place, la seconde n'en merite aucune : si elle n'est pas epinglee,
 * c'est peut-etre qu'on l'avait retiree expres. Les deux se ressemblent
 * pourtant trait pour trait quand on ne regarde que la liste affichee : dans
 * les deux cas, une app apparait qui n'etait pas la.
 *
 * LA MEMOIRE QUI LES SEPARE : {@link DOCK_KNOWN_PREF_KEY}, la liste des apps
 * que CE POSTE a deja vues installees. Elle suit `config.installedApps`, la
 * liste COMPLETE, jamais la liste affichee : l'installation est un fait de
 * configuration client, la visibilite est un fait de poste et de droits. Une
 * app absente de cette memoire est nouvelle, une app presente ne l'est plus,
 * quoi qu'il lui arrive ensuite.
 *
 * CE QU'IL NE FAIT JAMAIS : ejecter une app du dock (le plafond
 * {@link DOCK_APPS_MAX} borne l'ajout, il ne libere pas de place), changer
 * l'ordre choisi (les nouvelles apps sont posees en QUEUE), ou repecher une app
 * retiree a la main (elle est connue, donc jamais nouvelle). Dock plein : la
 * nouvelle app reste accessible par « Apps », et le panneau Dock des Parametres
 * montre sa ligne non epinglee avec sa raison ("dock plein").
 *
 * LE PREMIER CHARGEMENT APRES CE CHANGEMENT NE BOUSCULE RIEN. La memoire est
 * absente sur tous les postes existants : on la POSE alors telle qu'elle aurait
 * du etre (toutes les apps installees sont deja connues) et on ne touche a rien
 * d'autre. Un dock personnalise garde son contenu, y compris un dock
 * VOLONTAIREMENT VIDE (`dock.apps` a `[]`), qui est un choix et pas une absence
 * de choix.
 */

/**
 * Cle de pref, convention `<domaine>.<sujet>` du repo (`dock.apps`,
 * `dock.shortcuts`, `apps.hidden`). Le domaine est le dock : cette memoire ne
 * sert qu'a decider d'un epinglage, jamais de ce qui s'affiche ailleurs.
 */
export const DOCK_KNOWN_PREF_KEY = 'dock.known';

/** Ce qu'il y a a ecrire, ou `null` quand il n'y a rien a faire. */
export interface DockAutoPinWrite {
  /** La memoire mise a jour. Toujours presente : c'est elle qui fait foi. */
  known: string[];
  /**
   * La pref `dock.apps` mise a jour. ABSENTE quand il n'y a pas lieu d'y
   * toucher : dock non personnalise (le defaut montre deja les premieres apps),
   * plafond atteint, ou aucune app nouvelle affichable.
   */
  apps?: string[];
}

/** Les chaines d'un tableau venu du localStorage, sinon `null`. Jamais fatal. */
function listeDeChaines(valeur: unknown): string[] | null {
  return Array.isArray(valeur) ? valeur.filter((item): item is string => typeof item === 'string') : null;
}

/**
 * Ce qu'il faut ecrire pour qu'une app nouvellement installee prenne sa place.
 * PURE, testable sans React, et jamais fatale : les deux prefs viennent du
 * localStorage, donc d'un JSON que personne ne valide, et le dock est rendu par
 * le composant de la route RACINE, sans le moindre ErrorBoundary dans le depot.
 * Une valeur illisible se comporte comme une valeur absente.
 */
export function planDockAutoPin({
  appsPref,
  knownPref,
  installedIds,
  listedIds,
}: {
  /** La pref `dock.apps`, brute. */
  appsPref: unknown;
  /** La pref `dock.known`, brute. */
  knownPref: unknown;
  /** Les apps INSTALLEES, liste complete, dans l'ordre de la liste de l'org. */
  installedIds: readonly string[];
  /** Les apps AFFICHABLES ici et maintenant (droits, rebut, masquage DEV). */
  listedIds: ReadonlySet<string>;
}): DockAutoPinWrite | null {
  const known = listeDeChaines(knownPref);

  // BASELINE : la memoire n'existe pas encore (poste d'avant ce chantier, ou
  // pref effacee). Tout ce qui est installe est repute deja connu, et aucune
  // autre pref ne bouge. Sans ce cas, le premier chargement epinglerait d'un
  // coup toutes les apps installees, y compris celles qu'on avait retirees.
  if (known === null) return { known: [...installedIds] };

  const connues = new Set(known);
  const nouvelles = installedIds.filter((id) => !connues.has(id));
  if (nouvelles.length === 0) return null;
  const knownNext = [...known, ...nouvelles];

  const apps = listeDeChaines(appsPref);
  // DOCK NON PERSONNALISE : `resolveDockApps` montre deja les premieres apps
  // installees. Materialiser la pref ici FIGERAIT ce defaut, c'est-a-dire
  // fabriquerait la liste fermee qu'on est en train de corriger.
  if (apps === null) return { known: knownNext };

  // La place restante se compte sur ce qui s'EPINGLE VRAIMENT : un id que la
  // liste affichee ne resout pas (app masquee, preservee en queue de pref) ne
  // prend aucune place dans le rail, exactement comme dans `resolveDockApps`.
  const epingles = new Set<string>();
  for (const id of apps) {
    if (!listedIds.has(id) || epingles.has(id)) continue;
    epingles.add(id);
    if (epingles.size >= DOCK_APPS_MAX) break;
  }
  const place = DOCK_APPS_MAX - epingles.size;
  if (place <= 0) return { known: knownNext };

  const dansLaPref = new Set(apps);
  const aEpingler = nouvelles
    .filter((id) => listedIds.has(id) && !dansLaPref.has(id))
    .slice(0, place);
  if (aEpingler.length === 0) return { known: knownNext };
  // EN QUEUE, jamais ailleurs : l'ordre choisi par l'utilisateur ne bouge pas
  // d'un cran, et les ids preserves en queue (apps masquees) restent sautes a
  // la resolution.
  return { known: knownNext, apps: [...apps, ...aEpingler] };
}

/**
 * Le hook que monte le shell, une seule fois, dans le composant de la route
 * racine. Il n'affiche rien : il tient la memoire a jour et, le cas echeant,
 * epingle.
 *
 * IL ATTEND DE SAVOIR AVANT D'AGIR. Les droits de la fiche membre et l'etat
 * "rebut" du tampon de build arrivent apres le premier rendu : agir avant,
 * c'est decider sur une ignorance, donc epingler une app a laquelle le compte
 * n'a aucun droit. Tant que ce n'est pas su, rien ne s'ecrit.
 */
export function useDockAutoPin(
  installed: readonly ShellAppEntry[],
  listed: readonly ShellAppEntry[],
): void {
  const [appsPref, setAppsPref] = usePref<unknown>('dock.apps', null);
  const [knownPref, setKnownPref] = usePref<unknown>(DOCK_KNOWN_PREF_KEY, null);
  const deprecated = useDeprecatedAppIds();
  const acces = useAccesPort();
  const pret = deprecated.kind !== 'loading' && (acces === null || acces.pret);

  const installedIds = useMemo(() => installed.map((entry) => entry.id), [installed]);
  const listedIds = useMemo(() => new Set(listed.map((entry) => entry.id)), [listed]);

  useEffect(() => {
    if (!pret) return;
    const plan = planDockAutoPin({ appsPref, knownPref, installedIds, listedIds });
    if (plan === null) return;
    if (plan.apps !== undefined) setAppsPref(plan.apps);
    // La memoire s'ecrit EN DERNIER, mais surtout elle s'ecrit TOUJOURS : c'est
    // elle qui arrete la boucle, et c'est elle qui garantit qu'une app vue une
    // fois ne sera jamais reproposee.
    setKnownPref(plan.known);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pret, appsPref, knownPref, installedIds, listedIds]);
}
